import debounce from 'lodash.debounce'
import throttle from 'lodash/throttle'
import React, { useCallback, useEffect, useRef, useState } from 'react'

interface ITextArea {
  name: string
  autoHeight?: boolean
  placeholder?: string
  className?: string
  initialValue?: string
  onChange: (value) => void
  readOnly?: boolean
}

const autoHeightClassMapper = {
  true: 'overflow-hidden',
  false: 'overflow-auto',
}

const autoAdjustHeight = (element: HTMLElement) => {
  // Reset the height to the default (css) before getting the scrollHeight to know if we need to resize
  element.style.height = null
  const newHeight = element.scrollHeight
  element.style.height = `${newHeight}px`
}

const throttledAutoAdjustHeight = throttle((element) => {
  autoAdjustHeight(element)
}, 100)

export function FormTextArea({
  name,
  autoHeight = true,
  placeholder = '',
  className,
  initialValue = '',
  onChange,
  readOnly = false,
}: ITextArea) {
  const [value, setValue] = useState(initialValue || '')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const adjustHeight = useCallback(throttledAutoAdjustHeight, [])

  const updateValue = useCallback(
    debounce((textareaValue) => onChange(textareaValue), 200),
    []
  )

  useEffect(() => {
    updateValue(value)
    if (autoHeight) {
      adjustHeight(textareaRef.current)
    }
  }, [value])

  useEffect(() => {
    if (initialValue !== value) {
      setValue(initialValue)
    }
  }, [initialValue])

  return (
    <textarea
      id={`${name}-textarea`}
      ref={textareaRef}
      aria-label={`${name}-textarea`}
      name={name}
      value={value}
      readOnly={readOnly}
      onChange={(e) => setValue(e.currentTarget.value)}
      placeholder={placeholder}
      className={`w-full px-6 py-5 text-[14px] border-2 outline-0 rounded-lg outline-none shadow-none placeholder-black-80 resize-none ${
        autoHeightClassMapper[String(autoHeight)]
      } ${className ?? ''}`}
    />
  )
}
